import React, { useState, useEffect } from 'react';
import {
  Search,
  Shield,
  UserCircle,
  Users,
  Maximize,
  Minimize2,
  Minus,
  Plus,
  Command,
  LayoutGrid,
  Eye,
  Edit3
} from 'lucide-react';

interface OrgToolbarProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onSearch: (term: string) => void;
  searchResults: any[];
  onSelectUser: (id: string) => void;
  isEditMode: boolean;
  onToggleEditMode: () => void;
  onToggleFullScreen?: () => void;
  isFullscreen?: boolean;
  canManage?: boolean;
}

const OrgToolbar = ({
  onZoomIn,
  onZoomOut,
  onFitView,
  onSearch,
  searchResults,
  onSelectUser,
  isEditMode,
  onToggleEditMode,
  onToggleFullScreen,
  isFullscreen,
  canManage = false
}: OrgToolbarProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Handle Ctrl+K / Cmd+K search focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('org-search')?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <div className="absolute top-6 left-6 z-50 flex flex-col gap-4">
        {/* Mode Bar (Left) */}
        <div className="flex items-center gap-3 bg-white/80 backdrop-blur-md p-1 rounded-2xl shadow-premium border border-white/50">
          {/* View/Edit Mode Toggle */}
          {canManage && (
            <div className="flex p-1 bg-gray-100/50 rounded-xl">
              <button
                onClick={onToggleEditMode}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${!isEditMode
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
                disabled={!isEditMode}
              >
                <Eye size={14} />
                View
              </button>
              <button
                onClick={onToggleEditMode}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${isEditMode
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}
                `}
                disabled={isEditMode}
              >
                <Edit3 size={14} />
                Edit
              </button>
            </div>
          )}
        </div>

        {/* Control Tools */}
        <div className="flex flex-col gap-2 w-fit bg-white/80 backdrop-blur-md p-1.5 rounded-2xl shadow-premium border border-white/50">
          <button
            onClick={onZoomIn}
            className="p-2.5 text-gray-500 hover:bg-white hover:text-primary-600 rounded-xl transition-all shadow-hover"
          >
            <Plus size={20} />
          </button>
          <button
            onClick={onZoomOut}
            className="p-2.5 text-gray-500 hover:bg-white hover:text-primary-600 rounded-xl transition-all shadow-hover"
          >
            <Minus size={20} />
          </button>
          <div className="h-px bg-gray-100 mx-2" />
          <button
            onClick={onToggleFullScreen || onFitView}
            className={`p-2.5 rounded-xl transition-all shadow-hover ${isFullscreen
                ? 'bg-primary-50 text-primary-600'
                : 'text-gray-500 hover:bg-white hover:text-primary-600'
              }`}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize size={20} />}
          </button>
        </div>
      </div>

      {/* Search Input (Top Right) */}
      <div className="absolute top-6 right-6 z-50 flex flex-col gap-2 items-end">
        <div className="w-72 flex items-center bg-white/80 backdrop-blur-md p-1.5 rounded-2xl shadow-premium border border-white/50">
          <div className="relative group w-full">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors">
              <Search size={18} />
            </div>
            <input
              id="org-search"
              type="text"
              placeholder="Find employee..."
              className="w-full pl-10 pr-12 py-2.5 bg-gray-50/50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary-500/20 transition-all outline-none"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearch(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Search Results Dropdown */}
        {searchTerm && searchResults.length > 0 && (
          <div className="w-72 bg-white/90 backdrop-blur-lg rounded-2xl shadow-premium border border-white/50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="max-h-64 overflow-y-auto p-2 scrollbar-thin">
              {searchResults.map((user) => (
                <SearchResultItem
                  key={user.id}
                  user={user}
                  onSelect={onSelectUser}
                  onClear={() => setSearchTerm('')}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

interface SearchResultItemProps {
  user: any;
  onSelect: (id: string) => void;
  onClear: () => void;
}

const SearchResultItem = ({ user, onSelect, onClear }: SearchResultItemProps) => {

  return (
    <button
      onClick={() => {
        onSelect(user.id);
        onClear();
      }}
      className="w-full flex items-center gap-3 p-2.5 hover:bg-primary-50 rounded-xl transition-all group text-left border-b border-slate-50 last:border-none"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 truncate group-hover:text-primary-600 transition-colors mb-0 ">
          {user.fullName}
        </p>
      </div>
    </button>
  );
};

export default OrgToolbar;
